import type { ProductImage } from '@scayle/storefront-nuxt'
import {
  getAttributeValue,
  getImageFromList,
  isImageType,
} from '@scayle/storefront-nuxt'
import type { $Img, ImageSizesOptions } from '@nuxt/image'

export { getImageFromList, isImageType } from '@scayle/storefront-nuxt'

export type AssetType =
  | '_f'
  | '_1'
  | '_2'
  | '_3'
  | '_4'
  | '_5'
  | '_6'
  | '_7'
  | '_kb'

export const getFirstModelImage = (
  images: ProductImage[],
  index: number = 0,
) => {
  const baseImages = images.filter(
    (image: ProductImage) =>
      getAttributeValue(image.attributes, 'imageBackground') === 'grey',
  )

  const needsBreakerImage = (index + 1) % 5 === 0

  if (needsBreakerImage) {
    const breakerImage =
      baseImages.find(
        (image: ProductImage) =>
          getAttributeValue(image.attributes, 'image1stView') ===
          '1st_model_image',
      ) || getImage(baseImages, 'model_image', 'front')

    if (breakerImage) {
      return breakerImage
    }
  }

  return (
    getImage(baseImages, 'modeloutfit_image', 'front') ||
    getImage(baseImages, 'modeloutfit_image', 'front', true) ||
    getImage(baseImages, 'model_image', 'front', true) ||
    getImage(baseImages, 'bust_image', 'front', true) ||
    baseImages[0] ||
    images[0]
  )
}

export const getImageAssetType = (image: ProductImage) =>
  getAttributeValue(image?.attributes, 'assetType')

export const getFirstBustImage = (images: ProductImage[]) => {
  return (
    getImage(images, 'bust_image', 'front', true) || getFirstModelImage(images)
  )
}

/**
 * Order of asset types for images in product display page
 */
const IMAGE_ORDER = ['_f', '_1', '_2', '_3', '_4', '_5', '_6', '_7', '_kb']
const IMAGE_ORDER_MOBILE = [
  '_f',
  '_1',
  '_2',
  '_3',
  '_4',
  '_5',
  '_6',
  '_7',
  '_kb',
]
const IMAGE_FALLBACK = ['_kb']

/**
 * Get product image by specified assetType from image list with fallback
 */
export const getProductImage = (
  images: Array<ProductImage>,
  assetType: AssetType = '_f',
) => {
  const firstChoiceImage = getImageByAssetType(images, assetType)
  return firstChoiceImage || images[0]
}

/**
 * Get image by specified assetType from product image list
 */
export const getImageByAssetType = (
  images?: Array<ProductImage>,
  assetType: AssetType = '_f',
) => {
  return images?.find((image) => getImageAssetType(image) === assetType)
}

/**
 * Finds image object by assetType with value _5.
 * Returns a matched image object with key hash (url) .
 */
/**
 * Finds image object by assetType with value _5.
 * Returns a matched image object with key hash (url) .
 */
export const getHoverImage = (images?: Array<ProductImage>) =>
  getImageByAssetType(images, '_5')

/**
 * Looks for front image and returns it if not found will return first model image
 */
export const getFrontImage = (images: Array<ProductImage> = []) =>
  images.find((image) => getImageAssetType(image) === '_f') ||
  getFirstModelImage(images)

/**
 * Returns a list of images whose asset types are included in either IMAGE_ORDER or IMAGE_ORDER_MOBILE.
 */
export const filterOutRedundantImages = (
  images?: Array<ProductImage>,
  isMobile = false,
) => {
  const imageOrder = isMobile ? IMAGE_ORDER_MOBILE : IMAGE_ORDER

  const filterImagesByOrder = (order: Array<string>) => {
    return images?.filter((image) => {
      const assetType = getImageAssetType(image)
      return order.includes(assetType as string)
    })
  }

  const filteredImages = filterImagesByOrder(imageOrder)
  return filteredImages && filteredImages.length > 0
    ? filteredImages
    : filterImagesByOrder(IMAGE_FALLBACK)
}

/**
 * Sorts images by assetType.
 * Returns new array with sorted values.
 */
export const sortImagesByAssetType = (
  images?: Array<ProductImage>,
  isMobile = false,
) => {
  const imageOrder = isMobile ? IMAGE_ORDER_MOBILE : IMAGE_ORDER

  return [...(images || [])].sort((a: ProductImage, b: ProductImage) => {
    const aAssetType = getImageAssetType(a)
    const bAssetType = getImageAssetType(b)

    const aIndex = imageOrder.indexOf(aAssetType as string)
    const bIndex = imageOrder.indexOf(bAssetType as string)

    if (aIndex === -1) {
      return 1
    }

    if (bIndex === -1 || aIndex < bIndex) {
      return -1
    }

    return aIndex > bIndex ? 1 : 0
  })
}

const getDetailPageImages = (images: ProductImage[]) => {
  const baseImages = images.filter(
    (image: ProductImage) =>
      getAttributeValue(image.attributes, 'imageBackground') === 'grey',
  )

  let firstImage = baseImages.find(
    (image: ProductImage) =>
      getAttributeValue(image.attributes, 'image1stView') === '1st_model_image',
  )

  const hasModelImages = getImage(baseImages, 'model_image', 'front', true)

  let usingBustImages = false
  if (!firstImage && !hasModelImages) {
    usingBustImages = true
    firstImage = getImage(baseImages, 'bust_image', 'front', true)
  }

  const returnImages = [
    firstImage,
    getImage(baseImages, 'model_image', 'front'),
    getImage(baseImages, 'model_image', 'back'),
    ...baseImages.filter(
      (image: ProductImage) =>
        getAttributeValue(image.attributes, 'imageKind') === 'model_image' &&
        getAttributeValue(image.attributes, 'imageView') === undefined,
    ),
    ...baseImages.filter(
      (image: ProductImage) =>
        getAttributeValue(image.attributes, 'imageKind') ===
        'modeloutfit_image',
    ),

    ...(usingBustImages
      ? baseImages.filter(
          (image: ProductImage) =>
            getAttributeValue(image.attributes, 'imageKind') === 'bust_image',
        )
      : []),
    getImage(baseImages, 'detail_image', 'front'),
    getImage(baseImages, 'detail_image', 'back'),
    ...baseImages.filter(
      (image: ProductImage) =>
        getAttributeValue(image.attributes, 'imageKind') === 'detail_image' &&
        getAttributeValue(image.attributes, 'imageView') === undefined,
    ),
  ].filter((e) => !!e)

  return [...new Set(returnImages)]
}

const getBasketImage = (images: ProductImage[]) => {
  return getDetailPageImages(images)[0]?.hash
}

const getImage = (
  images: ProductImage[],
  imageKind: string,
  imageView: string,
  imageViewOptional = false,
) => {
  return (
    images.find(
      (image: ProductImage) =>
        getAttributeValue(image.attributes, 'imageKind') === imageKind &&
        getAttributeValue(image.attributes, 'imageView') === imageView,
    ) ||
    (imageViewOptional &&
      images.find(
        (image: ProductImage) =>
          getAttributeValue(image.attributes, 'imageKind') === imageKind,
      )) ||
    undefined
  )
}

export const parseSize = (input: string | number | undefined | null = '') => {
  if (typeof input === 'number') {
    return input
  }
  if (typeof input === 'string' && input.replace('px', '').match(/^\d+$/g)) {
    return parseInt(input, 10)
  }
}

const screenSizes = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1440,
  '3xl': 1680,
  '4xl': 1920,
}

export const getVariants = (
  $img: $Img,
  input: string,
  opts: ImageSizesOptions,
) => {
  const width = parseSize(opts.modifiers?.width)
  const height = parseSize(opts.modifiers?.height)
  const hwRatio = width && height ? height / width : 0
  const variants = []

  const sizes: Record<string, string> = {}

  // string => object
  if (typeof opts.sizes === 'string') {
    for (const entry of opts.sizes.split(/[\s,]+/).filter((e) => e)) {
      const parts = entry.split(':')
      if (parts.length !== 2) {
        continue
      }
      const [breakpoint, size] = parts
      if (!breakpoint || !size) {
        continue
      }
      sizes[breakpoint.trim()] = size.trim()
    }
  } else {
    Object.assign(sizes, opts.sizes)
  }

  for (const key in sizes) {
    const screenMaxWidth =
      screenSizes[key as keyof typeof screenSizes] || parseInt(key)

    let size = String(sizes[key])
    const isFluid = size.endsWith('vw')
    if (!isFluid && /^\d+$/.test(size)) {
      size = size + 'px'
    }
    if (!isFluid && !size.endsWith('px')) {
      continue
    }
    let _cWidth = parseInt(size)
    if (!screenMaxWidth || !_cWidth) {
      continue
    }
    if (isFluid) {
      _cWidth = Math.round((_cWidth / 100) * screenMaxWidth)
    }
    const _cHeight = hwRatio ? Math.round(_cWidth * hwRatio) : height
    variants.push({
      width: _cWidth,
      size,
      screenMaxWidth,
      media: `(max-width: ${screenMaxWidth}px)`,
      src: $img!(
        input,
        { ...opts.modifiers, width: _cWidth, height: _cHeight },
        opts,
      ),
    })
  }

  variants.sort((v1, v2) => v1.screenMaxWidth - v2.screenMaxWidth)
  return variants
}

export const getSizes = (
  variants: {
    width: number
    size: string
    screenMaxWidth: number
    media: string
    src: string
  }[],
) => {
  const defaultVar = variants[variants.length - 1]
  if (defaultVar) {
    defaultVar.media = ''
  }

  return {
    sizes: variants
      .map((v) => `${v.media ? v.media + ' ' : ''}${v.size}`)
      .join(', '),
    srcset: variants.map((v) => `${v.src} ${v.width}w`).join(', '),
    src: defaultVar?.src,
  }
}

export const showDividerTag = (index: number, arrayLength: number) => {
  return index >= 0 && arrayLength > 1 && index < arrayLength - 1
}

export default {
  getHoverImage,
  getImageFromList,
  isImageType,

  getProductImage,

  getDetailPageImages,

  getFirstModelImage,
  getBasketImage,
  getFrontImage,
  getModelImages: (images: ProductImage[]) => {
    return getDetailPageImages(images).slice(1)
  },

  getAttribute(image: ProductImage, key: string) {
    return (image.attributes?.[key]?.values as any)?.value
  },
}

import type { Product } from '@scayle/storefront-nuxt'
import { getAdvancedAttributeFirstValue } from './advancedAttribute'

/**
 * Sort + filter product images for display in the gallery.
 * Migrated from sources/mad-storefront/utils/image.ts so the lifted
 * MAD components in layers/base/ can resolve their import.
 */
export const getSortedProductImages = (
  images: Array<ProductImage>,
  product: Product,
  isMobile = false,
  limit = 7,
) => {
  if (images.length === 1) {
    return images
  }

  const sortedImages = sortImagesByAssetType(
    filterOutRedundantImages(images, isMobile),
    isMobile,
  )

  // Alba Moda products (article numbers starting with "A") need looser filtering
  if (
    getAdvancedAttributeFirstValue(product, 'mainOrderNumber')
      ?.toString()
      ?.startsWith('A') &&
    sortedImages.length < limit
  ) {
    for (const image of images) {
      if (sortedImages.length >= limit) break
      if (sortedImages.includes(image)) continue
      sortedImages.push(image)
    }
  }

  return sortedImages
}
